import React, { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

// Types
import { Props, UserManagement } from './types';

// Schema
import { UserManagementSchema } from './schema';

// Styles
import { Container, InputsContainer, AddUserButton } from './styles';

// Components
import InputText from '../../inputs/InputText';
import DropDownInput from '../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../inputs/DropDown/MultiSelectDropdown';

// Utils
import { useCacheFormInSessionStorage } from '../../../hooks/useCacheForm';
import { Typography } from '../../display/Typography';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefMultiSelect } from '../../inputs/DropDown/MultiSelectDropdown/types';
import { GroupTypes } from '../../display/ShowOT/WorkOrder/types';

export const AddUserForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
}: Props) => {
  const { t } = useTranslation(['forms', 'management']);
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<UserManagement>({
    defaultValues: {
      ...initialData,
      isActive: true,
      isCreated: true,
      teamRelationships: initialData.teamRelationships || [],
    },
    resolver: yupResolver(UserManagementSchema) as any,
  });

  useCacheFormInSessionStorage(namingPersistForm, control);

  // Refs
  const roleRef = useRef<ImperativeRefInput>(null);
  const concessionRef = useRef<ImperativeRefInput>(null);
  const teamsRef = useRef<ImperativeRefMultiSelect>(null);

  // States
  const [emailRepeatedError, setEmailRepeatedError] = useState<null | string>(
    null
  );

  // ✅ M2M: Watch role
  const selectedRole = watch('rolActive');

  // ✅ M2M: Team options for multi-select dropdown
  const teamOptions =
    initialData?.teams?.map((team) => ({
      label: team.name,
      value: team.id,
    })) || [];

  const onSubmit = async (data: UserManagement) => {
    const errorMessage = await onFormSubmit(data);
    if (errorMessage) {
      setEmailRepeatedError(errorMessage);
    } else {
      setEmailRepeatedError(null);
      reset({
        name: '',
        surName: '',
        email: '',
        rolActive: '',
        concession: '',
        id: '',
        teamRelationships: [],
      });
      roleRef.current?.clearSelected();
      concessionRef.current?.clearSelected();
      teamsRef.current?.clearSelected();
    }
  };

  const conOptions =
    initialData?.concessions?.reduce((acc, curr) => {
      acc.push({
        label: curr?.name || '',
        value: curr?.id || '',
      });

      return acc;
    }, [] as GroupTypes[]) || [];

  const rolOptions = [
    {
      label: t('management.roles.superAdmin'),
      value: 'super-admin',
    },
    {
      label: t('management.roles.roomTeam'),
      value: 'room-team',
    },
    {
      label: t('management.roles.coex'),
      value: 'coex',
    },
    {
      label: t('management.roles.capataz'),
      value: 'capataz',
    },
    {
      label: t('management.roles.tablet'),
      value: 'tablet',
    },
    {
      label: t('common:roadOperator'),
      value: 'operating-team',
    },
  ];

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <Typography size="L" variant="semiBold">
        {t('management.addUser')}
      </Typography>

      <InputsContainer>
        <InputText
          {...register('name')}
          label={t('common:labels.name')}
          typeInput={'normal'}
          inputSize="S"
          placeholder={t('placeholders.name')}
          borderError={!!errors.name?.message}
        />
        <InputText
          {...register('surName')}
          label={t('user.surname')}
          typeInput={'normal'}
          placeholder={t('user.surname')}
          borderError={!!errors.surName?.message}
        />
        <InputText
          {...register('email')}
          label={t('common:labels.email')}
          typeInput={'normal'}
          inputSize="M"
          placeholder={t('user.emailPlaceholder')}
          borderError={!!errors.email?.message}
        />
        <Controller
          control={control}
          name="rolActive"
          render={({ field: { onChange }, formState: { defaultValues } }) => {
            return (
              <DropDownInput
                placeholder={t('common:labels.select')}
                labelText={t('user.role')}
                defaultValue={defaultValues?.rolActive}
                inputSize={'M'}
                typeDropDown={'Default'}
                options={rolOptions}
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                borderError={!!errors.rolActive?.message}
                ref={roleRef}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="concession"
          render={({
            field: { onChange, value },
            formState: { defaultValues },
          }) => {
            // Si solo hay una concesión, auto-seleccionar y no mostrar nada
            if (conOptions.length === 1) {
              const concessionId = conOptions[0].value;
              if (value !== concessionId) {
                onChange(concessionId);
              }
              return <></>;
            }

            // Si hay múltiples concesiones, mostrar dropdown
            return (
              <DropDownInput
                placeholder={t('common:labels.select')}
                labelText={t('user.concession')}
                defaultValue={defaultValues?.concession}
                inputSize="XS"
                typeDropDown={'Default'}
                options={conOptions}
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                borderError={!!errors.concession?.message}
                ref={concessionRef}
              />
            );
          }}
        />

        {/* ✅ M2M: Team selection (optional for all roles) */}
        {teamOptions.length > 0 && (
          <Controller
            control={control}
            name="teamRelationships"
            render={({ field: { onChange, value } }) => (
              <MultiSelectDropdown
                placeholder={t('management:teams.selectTeams', 'Seleccionar equipos')}
                labelText={t('management:teams.assignedTeams', 'Equipos asignados')}
                inputSize="L"
                typeDropDown="Default"
                options={teamOptions}
                selectedValues={value || []}
                onChangeSelected={onChange}
                ref={teamsRef}
              />
            )}
          />
        )}

        <AddUserButton>⮕</AddUserButton>
        {emailRepeatedError && !errors.email?.message && (
          <Typography colorText="lightCoralRed" size="XS">
            {emailRepeatedError}
          </Typography>
        )}
      </InputsContainer>
    </Container>
  );
};
